import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { IoClose, IoImageOutline, IoSparkles } from "react-icons/io5";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { preprocessImage } from "../../utils/imagePreprocess";
import { trackCTAClicked } from "../../utils/analytics";
import { makeGuestStore, getOrCreateWidgetSession } from "../../utils/guestStorage";
import "../../styles/DesignPhotoModal.css";

const ALLOWED_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
]);

const MAX_BYTES = 10 * 1024 * 1024;

const IDEAS = [
  { label: "New patio", text: "Replace this with a sandstone patio" },
  { label: "Modern kitchen", text: "Give this kitchen a modern makeover" },
  { label: "Fresh paint", text: "Repaint this in a light, modern colour" },
  { label: "New driveway", text: "Show this with a new block paved driveway" },
];

const DESIGN_ASK = /\b(?:re)?design (?:this|it|my)\b|\bmock (?:this|it) up\b/i;

const asDesignRequest = (text) =>
  DESIGN_ASK.test(text) ? text : `Design this: ${text}`;

const readGuestSessionId = () => {
  return getOrCreateWidgetSession(makeGuestStore());
};

const DesignPhotoModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const fileInputRef = useRef(null);
  const textRef = useRef(null);
  const dialogRef = useRef(null);
  const uploadIdRef = useRef(0);

  const [preview, setPreview] = useState(null);
  const [uploaded, setUploaded] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [request, setRequest] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    if (dialogRef.current) dialogRef.current.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview]
  );

  if (!open) return null;

  const pickPhoto = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;

    if (file.type && !ALLOWED_TYPES.has(file.type.toLowerCase())) {
      setError("Please use a JPG, PNG, WEBP, GIF or HEIC photo.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("That photo is over 10MB. Please pick a smaller one.");
      return;
    }

    const uploadId = uploadIdRef.current + 1;
    uploadIdRef.current = uploadId;
    setError("");
    setUploaded(null);
    setPreview(URL.createObjectURL(file));
    setUploading(true);

    try {
      let uploadFile;
      try {
        uploadFile = await preprocessImage(file);
      } catch (preErr) {
        if (preErr && preErr.code === "IMAGE_TOO_SMALL") {
          if (uploadIdRef.current !== uploadId) return;
          setError(preErr.message);
          setPreview(null);
          return;
        }
        uploadFile = file;
      }

      const formData = new FormData();
      formData.append("image", uploadFile);
      const guestSessionId = readGuestSessionId();
      if (guestSessionId) formData.append("guest_session_id", guestSessionId);

      const response = await axiosPrivate.post("/chat/upload-image/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (uploadIdRef.current !== uploadId) return;

      const imageUrl = response.data && response.data.image_url;
      if (!imageUrl) throw new Error("No image_url in upload response");
      setUploaded({ imageUrl, displayUrl: response.data.display_url || imageUrl });
      if (textRef.current && !request.trim()) textRef.current.focus();
    } catch (err) {
      if (uploadIdRef.current !== uploadId) return;
      const serverMessage =
        err && err.response && err.response.status === 429 && err.response.data
          ? err.response.data.error
          : "";
      setError(serverMessage || "We couldn't upload that photo. Please try again.");
      setPreview(null);
    } finally {
      if (uploadIdRef.current === uploadId) setUploading(false);
    }
  };

  const canSubmit = Boolean(uploaded) && !uploading && request.trim().length > 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!uploaded) {
      setError("Add a photo of your space first.");
      return;
    }
    if (!request.trim()) {
      setError("Tell Percy what you'd like to change.");
      if (textRef.current) textRef.current.focus();
      return;
    }
    trackCTAClicked("homepage_design_popup", "chat");
    const message = asDesignRequest(request.trim());
    navigate(
      `/chat?q=${encodeURIComponent(message)}&source=homepage_design_popup`,
      { state: { initialImage: uploaded } }
    );
  };

  return createPortal(
    <div className="design-modal-overlay" onMouseDown={onClose}>
      <div
        ref={dialogRef}
        className="design-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="design-modal-title"
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="design-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          <IoClose aria-hidden="true" />
        </button>

        <h2 id="design-modal-title" className="design-modal-title">
          Design it with <span className="design-modal-highlight">Percy</span>
        </h2>
        <p className="design-modal-sub">
          Add a photo of your space and say what you'd like. Percy draws the new
          look, then prices the work.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="design-modal-file"
            onChange={handleFile}
            aria-label="Choose a photo of your space"
            tabIndex={-1}
          />

          {preview ? (
            <div className="design-modal-preview">
              <img src={preview} alt="Your space" />
              {uploading && (
                <div className="design-modal-uploading" role="status">
                  <span className="design-modal-spinner" aria-hidden="true" />
                  Uploading photo
                </div>
              )}
              <button
                type="button"
                className="design-modal-change"
                onClick={pickPhoto}
                disabled={uploading}
              >
                Change photo
              </button>
            </div>
          ) : (
            <button type="button" className="design-modal-drop" onClick={pickPhoto}>
              <IoImageOutline className="design-modal-drop-icon" aria-hidden="true" />
              <span className="design-modal-drop-title">Add a photo</span>
              <span className="design-modal-drop-hint">
                Garden, kitchen, bathroom, front of the house
              </span>
            </button>
          )}

          <label htmlFor="design-modal-request" className="design-modal-label">
            What would you like?
          </label>
          <textarea
            ref={textRef}
            id="design-modal-request"
            className="design-modal-text"
            rows={2}
            value={request}
            onChange={(e) => {
              setRequest(e.target.value);
              if (error) setError("");
            }}
            placeholder="E.g. Replace the lawn with a sandstone patio"
          />

          <div className="design-modal-ideas">
            {IDEAS.map((idea) => (
              <button
                key={idea.label}
                type="button"
                className="design-modal-idea"
                onClick={() => {
                  setRequest(idea.text);
                  if (error) setError("");
                }}
              >
                {idea.label}
              </button>
            ))}
          </div>

          {error && (
            <p className="design-modal-error" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="design-modal-submit"
            disabled={!canSubmit}
          >
            <IoSparkles aria-hidden="true" />
            Design it
          </button>
          <p className="design-modal-micro">Free · No signup</p>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default DesignPhotoModal;
